import { FC } from 'react';

import Input from 'components/Input/Input';

import InputSelectDropdown from '../../../InputSelectDropdown/InputSelectDropdown';
import { cn } from '../../../lib/utils';
import Typography from '../../../Typography/Typography';
import { FormField, FormItem } from '../../../ui';
import { MEDIA_TYPE_OPTIONS } from '../constants';

import { ExternalImageContentProps } from './types';

const externalImageInputFields = [
  {
    name: 'externalImageUrl',
    placeholder: 'https://',
    dataTestId: 'UploadContent-ImageURLInput',
    errorKey: 'externalImageUrl'
  },
  {
    name: 'mediaId',
    placeholder: '* ID Number',
    dataTestId: 'UploadContent-ImageIDInput',
    errorKey: 'mediaId'
  }
] as const;

const ExternalImageContent: FC<ExternalImageContentProps> = ({
  control,
  watch,
  errors,
  forceType
}) => {
  const imageRef = watch('externalImageUrl');
  const backgroundImage = imageRef ? `url(${imageRef})` : '';

  const renderPreviewContent = () => {
    if (imageRef) {
      return (
        <div
          data-testid="UploadContent-ReplaceImage"
          className="group absolute inset-0 flex items-center justify-center"
        />
      );
    }

    return (
      <Typography className="flex items-start text-p-s text-base-mutedForeground text-center p-4">
        <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-base-mutedForeground text-[10px] font-semibold leading-none">
          i
        </span>
        <span>
          Media is hosted externally. Appcharge does not upload or store this
          file. The image file is rendered from the original source and is not
          stored.
        </span>
      </Typography>
    );
  };

  return (
    <div className="flex flex-col gap-4 max-h-[600px]">
      <div
        className={cn(
          'relative w-full h-[300px] flex flex-col items-center justify-center bg-base-accent rounded-md border-dashed border border-indigo-200 overflow-hidden',
          imageRef && 'bg-contain bg-center bg-no-repeat border-transparent',
          imageRef && 'border-indigo-600 border-solid'
        )}
        style={{
          backgroundImage,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center'
        }}
      >
        {renderPreviewContent()}
      </div>
      <div className="w-full flex flex-col gap-y-4">
        {externalImageInputFields.map(
          ({ name, placeholder, dataTestId, errorKey }) => {
            const errorMessage = errors[errorKey]?.message;

            return (
              <FormField
                key={name}
                name={name}
                control={control}
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <Input
                      placeholder={placeholder}
                      data-testid={dataTestId}
                      value={value}
                      onChange={onChange}
                      isRequired={true}
                      state={errorMessage ? 'error' : 'default'}
                      description={errorMessage}
                    />
                  </FormItem>
                )}
              />
            );
          }
        )}
        <FormField
          name="type"
          control={control}
          render={({ field: { onChange } }) => (
            <FormItem>
              <InputSelectDropdown
                buttonDataTestId="UploadContent-TypeSelect"
                listDataTestId="UploadContent-TypeSelectList"
                disabled={!!forceType}
                defaultSelected={MEDIA_TYPE_OPTIONS.find(
                  (option) => option.value === watch('type')
                )}
                options={MEDIA_TYPE_OPTIONS}
                onOptionChange={onChange}
              />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
};

export default ExternalImageContent;
